import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Award, Users, Target, TrendingUp, BookOpen, Shield, Calendar, ArrowLeft, AlertTriangle, Globe, Briefcase, GraduationCap } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { useNavigate } from 'react-router-dom';
import { ISOProgressionPath } from '../components/training/ISOProgressionPath';
import { RelatedCoursesSection } from '../components/training/RelatedCoursesSection';
import { standards } from '../data/standards/standardsData';

export const ISO13485LeadAuditor = () => {
  const navigate = useNavigate();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "ISO 13485 Lead Auditor Training",
    "description": "ISO 13485 Lead Auditor Training is designed for professionals who want to develop advanced auditing skills to assess Medical Devices Quality Management Systems (MDQMS) in accordance with ISO 13485:2016. Covers risk-based auditing, regulatory compliance, and audit leadership.",
    "provider": {
      "@type": "Organization",
      "name": "ISDE Academy",
      "sameAs": "https://isdeacademy.com"
    },
    "coursePrerequisites": "Quality management experience in medical device industry recommended",
    "educationalLevel": "Advanced",
    "availableLanguage": "English",
    "inLanguage": "en",
    "audience": {
      "@type": "Audience",
      "audienceType": "Quality Managers, Regulatory Affairs Professionals, Internal Auditors, Medical Device Manufacturers, Consultants"
    },
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "Blended",
      "duration": "P5D",
      "location": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "India"
        }
      }
    },
    "teaches": [
      "ISO 13485:2016 requirements in depth",
      "Regulatory and quality system requirements interpretation",
      "Risk-based audit planning and execution",
      "Medical device process and controls evaluation",
      "Nonconformity identification and compliance risk assessment",
      "Professional audit report preparation",
      "Audit team leadership"
    ],
    "occupationalCredentialAwarded": {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "Professional Certification",
      "name": "ISO 13485 Lead Auditor Certification"
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is ISO 13485 Lead Auditor Training?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "It is a professional course that trains individuals to audit medical device quality management systems as per ISO 13485:2016. The training follows globally accepted auditing standards including ISO 19011 and ISO/IEC 17021-1."
        }
      },
      {
        "@type": "Question",
        "name": "Who can take ISO 13485 Lead Auditor training?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Quality professionals, auditors, regulatory staff, and medical device industry professionals can attend. It is ideal for Quality Managers, Regulatory Affairs Professionals, Internal Auditors, Compliance Officers, Medical Device Manufacturers, Consultants, and individuals aspiring to become IRCA-certified Lead Auditors."
        }
      },
      {
        "@type": "Question",
        "name": "Is ISO 13485 Lead Auditor certification globally valid?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, when issued by an accredited certification body, ISO 13485 Lead Auditor certification is internationally recognized, enabling professionals to work with international manufacturers, certification bodies, and consulting firms."
        }
      },
      {
        "@type": "Question",
        "name": "Can I attend ISO 13485 Lead Auditor training online?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, live online training is widely available with equal certification value. ISDE Academy offers both classroom and online training options."
        }
      },
      {
        "@type": "Question",
        "name": "Is ISO 13485 mandatory?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "ISO 13485 is not legally mandatory but is often required to meet regulatory and market access requirements from authorities such as CDSCO, FDA, EU MDR, and MDSAP."
        }
      }
    ]
  };

  const targetAudience = [
    "Quality Managers and Quality Engineers",
    "Regulatory Affairs Professionals",
    "Internal Auditors and Compliance Officers",
    "Medical Device Manufacturers",
    "Consultants and Freelance Auditors",
    "Professionals involved in supplier audits",
    "Individuals aspiring to become IRCA-certified Lead Auditors"
  ];

  const auditingStandards = [
    "ISO 13485:2016 – Medical devices – Quality management systems",
    "ISO 19011 – Guidelines for auditing management systems",
    "ISO/IEC 17021-1 – Requirements for audit and certification bodies"
  ];

  const courseObjectives = [
    "Understand ISO 13485 requirements in detail",
    "Interpret regulatory expectations related to medical devices",
    "Apply risk-based auditing techniques",
    "Conduct audits according to ISO 19011",
    "Prepare audit checklists and audit plans",
    "Identify and document nonconformities",
    "Prepare professional audit reports",
    "Lead audit teams effectively"
  ];

  const keyBenefits = [
    {
      title: "Career Advancement",
      description: "Certified ISO 13485 Lead Auditors are in high demand across medical device companies, certification bodies, and consulting firms."
    },
    {
      title: "Global Recognition",
      description: "ISO 13485 certification is recognized internationally, opening opportunities in India, Europe, the US, and other regulated markets."
    },
    {
      title: "Higher Earning Potential",
      description: "Lead auditors typically earn significantly more than internal auditors or quality executives."
    },
    {
      title: "Regulatory Expertise",
      description: "Training strengthens understanding of regulatory compliance, making professionals valuable during audits and inspections."
    },
    {
      title: "Consultancy Opportunities",
      description: "Certified professionals can work as independent consultants or trainers in the medical device sector."
    }
  ];

  const whyImportantReasons = [
    {
      title: "High Demand in the Medical Device Industry",
      description: "With increasing regulatory scrutiny from authorities such as CDSCO, FDA, EU MDR, and MDSAP, organizations require qualified auditors to ensure compliance.",
      icon: TrendingUp
    },
    {
      title: "Global Recognition",
      description: "ISO 13485 Lead Auditor certification is recognized worldwide, enabling professionals to work with international manufacturers, certification bodies, and consulting firms.",
      icon: Globe
    },
    {
      title: "Regulatory and Compliance Expertise",
      description: "Lead Auditors play a vital role in identifying gaps, ensuring compliance, and reducing regulatory risks in medical device operations.",
      icon: AlertTriangle
    },
    {
      title: "Career Growth and Professional Credibility",
      description: "Certified Lead Auditors are valued for their technical knowledge, auditing competence, and leadership skills in quality and regulatory roles.",
      icon: GraduationCap
    }
  ];

  const careerOpportunities = [
    "ISO 13485 Lead Auditor",
    "Quality Manager",
    "Regulatory Affairs Specialist",
    "Compliance Consultant",
    "Supplier Auditor",
    "Certification Body Auditor"
  ];

  const providerCriteria = [
    "Accreditation (IRCA / Exemplar Global)",
    "Trainer experience in medical device audits",
    "Course content depth",
    "Exam and certification process",
    "Industry reputation",
    "Post-training support"
  ];

  const industries = [
    "Medical device manufacturing",
    "Pharmaceutical companies",
    "Healthcare technology firms",
    "Regulatory consulting organizations"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="ISO 13485 Lead Auditor Training | Certified Medical QMS Course"
        description="Professional ISO 13485 Lead Auditor Training covering medical device QMS, audit techniques, compliance & certification with expert trainers."
        keywords="ISO 13485 Lead Auditor Training, ISO 13485 lead auditor, ISO 13485 training, medical device auditor, QMS medical devices, ISO 13485 certification, lead auditor course, medical device quality management, FDA compliance, EU MDR, CDSCO, MDSAP, IRCA certification, ISO 13485 auditor course, medical device QMS training, ISO 13485 Lead Auditor Training India, ISO 13485 Lead Auditor Training Chennai, ISO 13485 Lead Auditor Training Bangalore, ISO 13485 online training, certified medical QMS course India"
        canonical="/ISO-13485-Lead-Auditor-Training"
        structuredData={[structuredData, faqStructuredData]}
        breadcrumbs={[
          { name: 'ISO Training Services', path: '/training/iso-training-services' },
          { name: 'ISO 13485 Lead Auditor Training', path: '/ISO-13485-Lead-Auditor-Training' }
        ]}
      />

      {/* Hero Section with H1 */}
      <header className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-20 mt-20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              ISO 13485 Lead Auditor Training
            </h1>
            <p className="text-xl text-blue-100 max-w-4xl mx-auto leading-relaxed mb-4">
              ISO 13485 Lead Auditor Training is a specialized professional program designed for individuals who want to audit, implement, and maintain Quality Management Systems (QMS) for medical devices. ISO 13485 is the internationally recognized standard for quality management in the medical device industry, ensuring compliance with regulatory and safety requirements across global markets.
            </p>
            <p className="text-xl text-blue-100 max-w-4xl mx-auto leading-relaxed">
              With increasing regulatory scrutiny from authorities such as the FDA, EU MDR, and other international regulators, organizations require qualified lead auditors who can assess QMS effectiveness, identify nonconformities, and drive continual improvement. This makes ISO 13485 Lead Auditor Training a high-value certification for quality professionals, auditors, consultants, and regulatory experts.
            </p>
          </motion.div>
        </div>
      </header>

      {/* Back Button */}
      <nav className="bg-gray-50 py-6" aria-label="Breadcrumb">
        <div className="max-w-7xl mx-auto px-4">
          <Button
            onClick={() => navigate('/training/iso-training-services')}
            variant="outline"
            className="flex items-center gap-2 hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to ISO Training Services
          </Button>
        </div>
      </nav>

      {/* ISO Progression Path */}
      <ISOProgressionPath standard={standards['iso-13485']} currentLevel="leadAuditor" />

      {/* Related Courses */}
      <RelatedCoursesSection currentSlug="iso-13485" />

      <main className="max-w-7xl mx-auto px-4 py-12">
        <article>
          {/* What is ISO 13485 Lead Auditor Training? - H2 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                <BookOpen className="h-8 w-8 text-blue-600" aria-hidden="true" />
                What is ISO 13485 Lead Auditor Training?
              </h2>
              <Card>
                <CardContent className="p-8">
                  <p className="text-lg text-gray-700 leading-relaxed mb-6">
                    <strong>ISO 13485 Lead Auditor Training</strong> is a professional certification program that equips individuals with the competence required to audit medical device quality management systems based on ISO 13485:2016.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed mb-6">
                    The training follows globally accepted auditing standards, including:
                  </p>
                  <ul className="space-y-3 mb-6 list-none">
                    {auditingStandards.map((standard, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" aria-hidden="true" />
                        <span className="text-gray-700 font-medium">{standard}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-lg text-gray-700 leading-relaxed font-semibold bg-blue-50 p-4 rounded-lg">
                    After successful completion, participants are qualified to perform first-party, second-party, and third-party audits of medical device organizations.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </section>

          {/* Course Objectives - H3 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                <Target className="h-7 w-7 text-blue-600" aria-hidden="true" />
                ISO 13485 Lead Auditor Training Course Objectives
              </h3>
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-8">
                  <p className="text-lg text-gray-700 mb-6">
                    A comprehensive ISO 13485 Lead Auditor Training course aims to help participants:
                  </p>
                  <ul className="grid md:grid-cols-2 gap-4 list-none mb-6">
                    {courseObjectives.map((objective, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" aria-hidden="true" />
                        <span className="text-gray-700">{objective}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-lg text-gray-700 font-semibold">
                    By the end of the training, participants gain the competence required to perform audits independently and confidently.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </section>

          {/* Why ISO 13485 Lead Auditor Training is Important - H3 with H4 items */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold mb-8 text-gray-900 flex items-center gap-3">
                <AlertTriangle className="h-7 w-7 text-blue-600" aria-hidden="true" />
                Why ISO 13485 Lead Auditor Training is Important
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                {whyImportantReasons.map((reason, index) => {
                  const IconComponent = reason.icon;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Card className="h-full hover:shadow-lg transition-shadow">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="bg-blue-100 p-3 rounded-lg flex-shrink-0">
                              <IconComponent className="h-6 w-6 text-blue-600" aria-hidden="true" />
                            </div>
                            <div className="flex-1">
                              <h4 className="text-xl font-bold mb-3 text-gray-900">{reason.title}</h4>
                              <p className="text-gray-700 leading-relaxed">{reason.description}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </section>

          {/* Who Should Attend - H2 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                <Users className="h-8 w-8 text-blue-600" aria-hidden="true" />
                Who Should Attend ISO 13485 Lead Auditor Training?
              </h2>
              <Card>
                <CardContent className="p-8">
                  <p className="text-lg text-gray-700 mb-6">ISO 13485 Lead Auditor Training is ideal for:</p>
                  <ul className="grid md:grid-cols-2 gap-4 list-none">
                    {targetAudience.map((audience, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" aria-hidden="true" />
                        <span className="text-gray-700">{audience}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-lg text-gray-700 mt-6">
                    Whether you work in manufacturing, design, distribution, or regulatory consulting, this training adds strong credibility to your professional profile.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </section>

          {/* Benefits - H3 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold mb-8 text-gray-900 flex items-center gap-3">
                <TrendingUp className="h-7 w-7 text-blue-600" aria-hidden="true" />
                Benefits of ISO 13485 Lead Auditor Training
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                {keyBenefits.map((benefit, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="bg-blue-100 p-3 rounded-lg flex-shrink-0">
                          <span className="text-blue-600 font-bold text-lg" aria-hidden="true">{index + 1}</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-xl font-bold mb-3 text-gray-900">{benefit.title}</h4>
                          <p className="text-gray-700">{benefit.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </section>

          {/* How to Choose Provider - H3 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                <BookOpen className="h-7 w-7 text-blue-600" aria-hidden="true" />
                How to Choose the Right ISO 13485 Lead Auditor Training Provider
              </h3>
              <Card>
                <CardContent className="p-8">
                  <p className="text-lg text-gray-700 mb-6">
                    When selecting a training provider, consider:
                  </p>
                  <ul className="grid md:grid-cols-2 gap-4 list-none">
                    {providerCriteria.map((criteria, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" aria-hidden="true" />
                        <span className="text-gray-700">{criteria}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-lg text-gray-700 mt-6 font-semibold">
                    Choosing the right provider ensures quality learning and global recognition of your certification.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </section>

          {/* Career Opportunities - H3 */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 flex items-center gap-3">
                <Award className="h-7 w-7 text-blue-600" aria-hidden="true" />
                Career Opportunities After ISO 13485 Lead Auditor Training
              </h3>
              <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                <CardContent className="p-8">
                  <p className="text-lg text-gray-700 mb-6">
                    After certification, professionals can work as:
                  </p>
                  <ul className="grid md:grid-cols-3 gap-4 mb-8 list-none">
                    {careerOpportunities.map((opportunity, index) => (
                      <li key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                        <CheckCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" aria-hidden="true" />
                        <span className="text-gray-700 font-semibold">{opportunity}</span>
                      </li>
                    ))}
                  </ul>
                  <h4 className="text-xl font-bold mb-4 text-gray-900">Industries include:</h4>
                  <ul className="grid md:grid-cols-2 gap-4 list-none">
                    {industries.map((industry, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Shield className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" aria-hidden="true" />
                        <span className="text-gray-700">{industry}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </section>

          {/* FAQ Section - visible for SEO + AEO */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900 flex items-center gap-3">
                <Briefcase className="h-8 w-8 text-blue-600" aria-hidden="true" />
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqStructuredData.mainEntity.map((faq, index) => (
                  <Card key={index} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.name}</h3>
                      <p className="text-gray-700 leading-relaxed">{faq.acceptedAnswer.text}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </section>
        </article>

        {/* CTA Section */}
        <aside>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
              <CardContent className="p-12 text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Ready to Become a Certified ISO 13485 Lead Auditor?
                </h2>
                <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
                  Join medical device quality professionals who have advanced their careers with our internationally recognized ISO 13485 Lead Auditor certification program.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button
                    onClick={() => navigate('/training-calendar')}
                    className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg"
                    size="lg"
                  >
                    <Calendar className="h-5 w-5 mr-2" aria-hidden="true" />
                    View Training Schedule
                  </Button>
                  <Button
                    onClick={() => navigate('/contact')}
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-6 text-lg"
                    size="lg"
                  >
                    Request More Information
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </aside>
      </main>
    </div>
  );
};
